import { FC, ReactElement, useState } from "react";
import { 
  Box, 
  Typography, 
  Menu, 
  MenuItem, 
  styled
} from "@mui/material";
import { KeyboardArrowDown, SmartToy, Gavel, Category } from "@mui/icons-material";
import { ChatCategory } from "@/types/chat";

interface ChatAgentSelectorProps {
  category: ChatCategory;
  onCategoryChange: (category: ChatCategory) => void;
  disabled?: boolean;
  dense?: boolean;
  iconOnlyWhenClosed?: boolean;
  slim?: boolean;
}

const AGENT_OPTIONS = [
  {
    value: ChatCategory.GENERAL,
    label: "General Assistant",
    description: "Comprehensive trade assistance",
    icon: <Category sx={{ fontSize: 18 }} />,
    color: "#2196F3",
  },
  {
    value: ChatCategory.HTS_CLASSIFICATION,
    label: "HTS Expert",
    description: "Product classification expert",
    icon: <SmartToy sx={{ fontSize: 18 }} />,
    color: "#4CAF50",
  },
  {
    value: ChatCategory.CBP_AUDIT,
    label: "CBP Audit Expert",
    description: "Compliance & audit specialist",
    icon: <Gavel sx={{ fontSize: 18 }} />,
    color: "#FF9800",
  },
];

const StyledButton = styled("button")<{
  disabled?: boolean;
  dense?: boolean;
  slim?: boolean;
}>(({ disabled, dense, slim }) => ({
  backgroundColor: "#FFFFFF",
  border: "1px solid #E8EBED",
  borderRadius: slim ? "6px" : "8px",
  padding: dense ? "4px 10px" : "6px 12px",
  display: "flex",
  alignItems: "center",
  gap: dense ? "6px" : "8px",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.6 : 1,
  transition: "all 0.2s ease",
  fontFamily: "Inter Display, sans-serif",
  fontSize: dense ? "13px" : "14px",
  fontWeight: 500,
  lineHeight: dense ? "18px" : "20px",
  color: "#33404D",
  outline: "none",
  "&:hover": {
    backgroundColor: disabled ? "#FFFFFF" : "#F9FAFB",
    borderColor: disabled ? "#E8EBED" : "#D1D5DB",
  },
}));

const ChatAgentSelector: FC<ChatAgentSelectorProps> = ({
  category,
  onCategoryChange,
  disabled = false,
  dense = false,
  iconOnlyWhenClosed = false,
  slim = false,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentAgent = AGENT_OPTIONS.find(agent => agent.value === category) || AGENT_OPTIONS[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAgentSelect = (newCategory: ChatCategory) => {
    onCategoryChange(newCategory);
    handleClose();
  };

  return (
    <Box sx={{ display: "inline-block" }}>
      <StyledButton onClick={handleClick} disabled={disabled} dense={dense} slim={slim}>
        {/* Icon - keeping color for identity */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: currentAgent.color,
            fontSize: dense ? 16 : 18,
          }}
        >
          {currentAgent.icon}
        </Box>

        {/* Label (optional for compact/icon-only mode) */}
        {!(iconOnlyWhenClosed && !open) && (
          <span>{currentAgent.label}</span>
        )}

        {/* Chevron */}
        {!disabled && (
          <Box sx={{ display: "flex", alignItems: "center", color: "#33404D" }}>
            <KeyboardArrowDown sx={{ fontSize: dense ? 14 : 16 }} />
          </Box>
        )}
      </StyledButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "8px",
            boxShadow: "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
            border: "1px solid #EAECF0",
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {AGENT_OPTIONS.map((agent) => (
          <MenuItem
            key={agent.value}
            onClick={() => handleAgentSelect(agent.value)}
            selected={agent.value === category}
            sx={{
              py: dense ? 1 : 1.5,
              px: dense ? 1.5 : 2,
              gap: 1.5,
              fontFamily: "Inter Display, sans-serif",
              fontSize: dense ? "13px" : "14px",
              color: "#33404D",
              "&:hover": {
                backgroundColor: "#F9FAFB",
              },
              "&.Mui-selected": {
                backgroundColor: "#F9FAFB",
                color: "#0B494B",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#F0FDFD",
                },
              },
            }}
          >
            <Box sx={{ color: agent.color, display: "flex", alignItems: "center", fontSize: 18 }}>
                {agent.icon}
            </Box>
            <Box>
                <Typography sx={{ fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", lineHeight: "20px" }}>
                    {agent.label}
                </Typography>
                <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#667085", lineHeight: "18px" }}>
                    {agent.description}
                </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ChatAgentSelector;
